<template>
  <n-modal
    v-model:show="visible"
    preset="card"
    title="System Notifications"
    class="w-full max-w-md mx-4"
    :bordered="false"
    :closable="!loading"
    :mask-closable="!loading"
  >
    <!-- 通知列表内容 -->
    <div class="max-h-96 overflow-y-auto">
      <!-- 加载状态 -->
      <div v-if="loading" class="flex justify-center py-8">
        <n-spin size="medium" />
      </div>
      
      <!-- 空状态 -->
      <div v-else-if="notifications.length === 0" class="text-center py-8">
        <n-icon size="48" class="text-gray-300 mb-4">
          <BellOffIcon />
        </n-icon>
        <p class="text-gray-500 text-sm">No notifications yet</p>
      </div>
      
      <!-- 通知列表 -->
      <div v-else class="space-y-3">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="p-3 rounded-lg border cursor-pointer transition-colors hover:bg-gray-50"
          :class="getNotificationCardClass(notification)"
          @click="handleNotificationClick(notification)"
        >
          <div class="flex items-start">
            <!-- 未读状态指示器 -->
            <div
              class="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0"
              :class="notification.is_read ? 'bg-gray-400' : 'bg-blue-500'"
            ></div>
            
            <!-- 通知内容 -->
            <div class="flex-1 min-w-0">
              <!-- 通知类型图标和标题 -->
              <div class="flex items-center mb-1">
                <n-icon size="16" class="mr-2" :class="getNotificationIconClass(notification.type)">
                  <component :is="getNotificationIcon(notification.type)" />
                </n-icon>
                <h4 class="text-sm font-medium text-gray-900 truncate">
                  {{ notification.title }}
                </h4>
              </div>
              
              <!-- 通知消息 -->
              <p class="text-sm text-gray-600 mb-2 line-clamp-2">
                {{ notification.message }}
              </p>
              
              <!-- 时间戳 -->
              <p class="text-xs text-gray-500">
                {{ formatNotificationTime(notification.created_at) }}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 加载更多按钮 -->
      <div v-if="hasMore && !loading" class="text-center pt-4">
        <n-button
          size="small"
          quaternary
          @click="loadMore"
          :loading="loadingMore"
        >
          Load more
        </n-button>
      </div>
    </div>
    
    <!-- 底部操作按钮 -->
    <template #footer>
      <div class="flex justify-between">
        <n-button
          v-if="unreadCount > 0"
          size="small"
          type="primary"
          quaternary
          @click="markAllAsRead"
          :loading="markingAllAsRead"
        >
          Mark all as read ({{ unreadCount }})
        </n-button>
        <div v-else></div>
        
        <n-button @click="closeModal">
          Close
        </n-button>
      </div>
    </template>
  </n-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useMessage } from 'naive-ui'
import { 
  NModal, 
  NIcon, 
  NSpin, 
  NButton
} from 'naive-ui'
import {
  BellOff as BellOffIcon,
  CreditCard as TransactionIcon,
  Wallet as RechargeIcon,
  ArrowUpRight as WithdrawIcon,
  Settings as SystemIcon,
  Leaf as ProjectIcon,
  Crown as MembershipIcon
} from '@vicons/tabler'

import type { SystemNotification, NotificationType } from '@/types/personal-center'
import { formatNotificationTime } from '@/utils/personal-center'
import { UserService } from '@/services/userService'

// Props 定义
interface Props {
  show: boolean
  initialNotifications?: SystemNotification[]
  initialUnreadCount?: number
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  initialNotifications: () => [],
  initialUnreadCount: 0
})

// Emits 定义
interface Emits {
  (e: 'update:show', value: boolean): void
  (e: 'notification-read', notificationId: number): void
  (e: 'all-notifications-read'): void
  (e: 'unread-count-updated', count: number): void
}

const emit = defineEmits<Emits>()

// 组合式API
const message = useMessage()

// 响应式数据
const visible = computed({
  get: () => props.show,
  set: (value) => emit('update:show', value)
})

const notifications = ref<SystemNotification[]>([])
const loading = ref(false)
const loadingMore = ref(false)
const markingAllAsRead = ref(false)
const currentPage = ref(1)
const totalPages = ref(1)
const unreadCount = ref(0)

// 计算属性
const hasMore = computed(() => currentPage.value < totalPages.value)

// 方法
const loadNotifications = async (page = 1, append = false) => {
  try {
    if (page === 1) {
      loading.value = true
    } else {
      loadingMore.value = true
    }

    const response = await UserService.getSystemNotifications(page, 10)
    
    if (append) {
      notifications.value.push(...response.notifications)
    } else {
      notifications.value = response.notifications
    }
    
    currentPage.value = response.current_page
    totalPages.value = response.last_page
    unreadCount.value = response.unread_count
    
    // 通知父组件未读数量更新
    emit('unread-count-updated', response.unread_count)
    
  } catch (error) {
    console.error('加载系统通知失败:', error)
    message.error('Failed to load notifications')
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

const loadMore = async () => {
  if (hasMore.value && !loadingMore.value) {
    await loadNotifications(currentPage.value + 1, true)
  }
}

const handleNotificationClick = async (notification: SystemNotification) => {
  // 如果通知未读，标记为已读
  if (!notification.is_read) {
    await markNotificationAsRead(notification.id)
  }
  
  // 如果通知有操作链接，可以在这里处理跳转
  if (notification.action_url) {
    // 这里可以添加路由跳转逻辑
    console.log('Navigate to:', notification.action_url)
  }
}

const markNotificationAsRead = async (notificationId: number) => {
  try {
    await UserService.markNotificationAsRead(notificationId)
    
    // 更新本地状态
    const notification = notifications.value.find(n => n.id === notificationId)
    if (notification && !notification.is_read) {
      notification.is_read = true
      notification.read_at = new Date().toISOString()
      unreadCount.value = Math.max(0, unreadCount.value - 1)
      
      // 通知父组件
      emit('notification-read', notificationId)
      emit('unread-count-updated', unreadCount.value)
    }
    
  } catch (error) {
    console.error('标记通知已读失败:', error)
    message.error('Failed to mark notification as read')
  }
}

const markAllAsRead = async () => {
  try {
    markingAllAsRead.value = true
    await UserService.markAllNotificationsAsRead()
    
    // 更新本地状态
    notifications.value.forEach(notification => {
      if (!notification.is_read) {
        notification.is_read = true
        notification.read_at = new Date().toISOString()
      }
    })
    
    unreadCount.value = 0
    
    // 通知父组件
    emit('all-notifications-read')
    emit('unread-count-updated', 0)
    
    message.success('All notifications marked as read')
    
  } catch (error) {
    console.error('标记所有通知已读失败:', error)
    message.error('Failed to mark all notifications as read')
  } finally {
    markingAllAsRead.value = false
  }
}

const closeModal = () => {
  visible.value = false
}

// 获取通知类型对应的图标
const getNotificationIcon = (type: NotificationType) => {
  const iconMap = {
    transaction: TransactionIcon,
    recharge: RechargeIcon,
    withdraw: WithdrawIcon,
    system: SystemIcon,
    project: ProjectIcon,
    membership: MembershipIcon
  }
  return iconMap[type] || SystemIcon
}

// 获取通知类型对应的图标样式类
const getNotificationIconClass = (type: NotificationType) => {
  const classMap = {
    transaction: 'text-blue-600',
    recharge: 'text-green-600',
    withdraw: 'text-orange-600',
    system: 'text-gray-600',
    project: 'text-emerald-600',
    membership: 'text-purple-600'
  }
  return classMap[type] || 'text-gray-600'
}

// 获取通知卡片样式类
const getNotificationCardClass = (notification: SystemNotification) => {
  if (notification.is_read) {
    return 'bg-gray-50 border-gray-200'
  } else {
    return 'bg-blue-50 border-blue-200'
  }
}

// 监听弹窗显示状态
watch(visible, (newValue) => {
  if (newValue) {
    // 弹窗打开时加载通知
    loadNotifications(1, false)
  } else {
    // 弹窗关闭时重置状态
    currentPage.value = 1
    totalPages.value = 1
  }
})

// 初始化
onMounted(() => {
  // 如果有初始数据，使用初始数据
  if (props.initialNotifications.length > 0) {
    notifications.value = [...props.initialNotifications]
    unreadCount.value = props.initialUnreadCount
  }
})
</script>

<style scoped>
/* 文本截断样式 */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 滚动条样式 */
.max-h-96::-webkit-scrollbar {
  width: 4px;
}

.max-h-96::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 2px;
}

.max-h-96::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.max-h-96::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* 响应式设计 */
@media (min-width: 768px) {
  .max-w-md {
    max-width: 32rem;
  }
}

@media (min-width: 1024px) {
  .mx-4 {
    margin-left: 2rem;
    margin-right: 2rem;
  }
}
</style>